import {useContext} from 'react'
import {createPortal} from 'react-dom'
import {storage, STORAGE_KEYS} from '@/utils/storage'
import {BookingModalContext} from '@/customComponents/BookRideSteps/Components/BookingModalShell.jsx'
import {closeIcon, longArrowLeft, bigCloseLight, bigClose, chevronLeft, boldCircleCross} from '@img/index.js'

/**
 * Unified ModalHeader.
 *
 * Two call styles are supported — the renderer picks one based on the props
 * you pass:
 *
 * 1. Simple (dashboard's native modal):
 *    <ModalHeader title="..." onClose={...} onBack={...} titleIcon={...} bottomTitle="..."/>
 *
 * 2. Booking flow (ported from plugin):
 *    <ModalHeader
 *      title="..."
 *      extraClasses="grey|less-space|no-space|extra-space|spacial-request|first|less-font|no-background|pt-0|pb-0|mt-0|mb-0|dark|transparent"
 *      totalSteps={3}
 *      cancelButton={true} cancelButtonType="text|icon"
 *      buttonStyle="plain|elegant|modern"
 *      backStep="travelingDateTime" showBackButton={true}
 *      setStep={setStep} closeModal={closeModal}
 *      leftIcon={img} leftIconAlt="..."
 *      enableEditModeOnBackButton={true}
 *      ignoreEditMode={false}
 *    />
 */
export function ModalHeader(props) {
    const {
        title,
        // Simple-only props
        titleIcon,
        bottomTitle,
        onClose,
        // Simple variant: renders its own back button. Booking variant: overrides where
        // the back arrow goes (see handleBack) — for a header whose back moves within the
        // CURRENT screen rather than to another step, e.g. a popup that is itself a small
        // wizard (the flight date → flight type screens in smartPickupRecommendation).
        // Takes precedence over backStep, so ✕ stays free to close the popup.
        onBack,
        // Simple-only: override the default back/close icons + use the compact
        // outline (bordered-circle) button style — see the cancellation modal.
        backIconSrc,
        closeIconSrc,
        outlineButtons = false,
        // Booking-flow props
        leftIcon,
        leftIconAlt,
        extraClasses,
        totalSteps = 0,
        cancelButton,
        cancelButtonType,
        /*
         * Show the ✕ on DESKTOP ONLY (≥ md).
         *
         * For a step whose mobile shell already gives the passenger a way out — the sheet
         * closes by dragging or tapping the backdrop — while the desktop dialog has only
         * the back arrow, which walks the booking backwards instead of leaving it. Uses the
         * same `max-md` breakpoint this header already hides the title and progress bar at,
         * so the whole row appears and disappears on one line.
         */
        cancelDesktopOnly = false,
        // Booking-flow back/close visual style — the booking-variant counterpart of
        // the simple variant's `outlineButtons`. See the block below.
        buttonStyle,
        backStep,
        showBackButton,
        setStep,
        closeModal,
        enableEditModeOnBackButton = true,
        ignoreEditMode = false,
        inline = false,
    } = props

    const isBookingVariant =
        leftIcon !== undefined ||
        extraClasses !== undefined ||
        totalSteps > 0 ||
        cancelButton !== undefined ||
        cancelButtonType !== undefined ||
        buttonStyle !== undefined ||
        backStep !== undefined ||
        showBackButton !== undefined ||
        setStep !== undefined ||
        closeModal !== undefined

    if (!isBookingVariant) {
        const simpleBtnCls = outlineButtons
            ? 'cursor-pointer flex items-center justify-center w-9 h-9 rounded-full bg-[#F3F4F6] border border-[#CFCDCD]'
            : 'cursor-pointer flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-[0px_4px_32px_0px_rgba(32,30,32,0.12)]'
        /*
         * ⚠️ THE PORTAL'S BACK ARROW, AT ITS OWN SIZE — the same asset and the same
         * rendering as the booking header's, so "back" is one icon across the product.
         *
         * It used to default to the shared RIGHT arrow rotated 90°, which points DOWN: a
         * generic affordance standing in for a control whose entire meaning is direction.
         * Callers worked around it by passing their own, and the `w-4 h-4` that came with
         * that override then drew whatever they passed at chevron size — which is why a
         * proper arrow still came out thin and small here while the booking flow's looked
         * right. Both halves of that are gone: the default is correct, and an override is
         * drawn at its natural size like every other icon in this header.
         */
        const backSrc = backIconSrc || longArrowLeft
        const backImgCls = ''
        const closeSrc = closeIconSrc || closeIcon
        const closeImgCls = closeIconSrc ? 'w-4 h-4 max-md:w-3 max-md:h-3' : ''
        return (
            <>
                <div className={`flex items-start justify-between mb-6 max-md:mb-4 ${!titleIcon ? 'flex items-center' : ''}`}>
                    <div className="w-9">
                        {onBack && (
                            <button type="button" onClick={onBack} className={simpleBtnCls}>
                                <img alt="Go back" src={backSrc} className={backImgCls}/>
                            </button>
                        )}
                    </div>
                    <h4 className="font-semibold mb-0! text-center text-2xl max-md:text-lg!">
                        {titleIcon && (<div className="flex justify-center mb-4">{titleIcon}</div>)}
                        {title}
                    </h4>
                    <div className="w-12 flex justify-end">
                        {onClose && (
                            <button type="button" onClick={onClose} className={simpleBtnCls}>
                                <img alt="cross" src={closeSrc} className={closeImgCls}/>
                            </button>
                        )}
                    </div>
                </div>
                {bottomTitle && (<h3 className="font-bold text-center mb-10!">{bottomTitle}</h3>)}
            </>
        )
    }

    // Booking variant
    const isEditMode = storage.get(STORAGE_KEYS.EDIT_STEP, false) === true

    const handleBack = () => {
        if (onBack) { onBack(); return }
        if (backStep) { setStep?.(backStep); return }
        closeModal?.()
    }

    const cls = (extraClasses || '').toLowerCase()
    const has = (token) => cls.includes(token)

    const isTransparent = has('transparent')
    const isNoBackground = has('no-background')
    const isGrey = has('grey')
    const isDark = has('dark')
    const isLessSpace = has('less-space')
    const isNoSpace = has('no-space')
    const isExtraSpace = has('extra-space')
    const isSpacialRequest = has('spacial-request')
    const isLessFont = has('less-font')
    const isPt0 = has('pt-0')
    const isMt0 = has('mt-0')
    const isPb0 = has('pb-0')
    const isMb0 = has('mb-0')
    // Opt out of the mobile side-padding shrink (keep the full 30px on all widths).
    const isFullPadX = has('full-pad-x')

    const wrapperBg = isTransparent || isNoBackground
        ? 'bg-transparent'
        : isGrey || isDark
        ? 'bg-[#efefef]'
        : 'bg-white'

    const wrapperMargin = isNoSpace
        ? '!mb-0'
        : isExtraSpace
        ? 'mb-[50px]'
        : isSpacialRequest
        ? 'mb-[15px]'
        : isLessSpace
        ? 'mb-2.5'
        : isMb0
        ? 'mb-0'
        : 'mb-0'

    const wrapperTop = isMt0 ? 'mt-0' : 'mt-0'
    const wrapperPadY = isLessFont ? 'pt-0' : (isPt0 ? 'pt-0' : 'pt-[30px] max-md:pt-[15px]')
    const wrapperPadB = isPb0 ? 'pb-0' : 'pb-[30px] max-md:pb-[15px]'

    const bookingShell = useContext(BookingModalContext)
    const insideShell = !inline && !!bookingShell?.headerEl
    // Stacked overlay (mobile nested popup): render a close (✕) instead of the step's back arrow.
    const overlayMode = bookingShell?.overlay ?? false

    const wrapperCls = [
        // Only stick when rendered INLINE (no booking shell). Inside the shell
        // the header slot is a flex `shrink-0` row outside the scroll area.
        insideShell ? 'relative' : 'sticky top-0 z-[99] relative',
        wrapperBg,
        isFullPadX ? 'px-[30px]' : 'px-[30px] max-md:px-[15px]',
        wrapperTop,
        wrapperMargin,
        wrapperPadY,
        wrapperPadB,
    ].join(' ')

    // Back/close visual style. Defaults to 'elegant' = the existing white-circle look
    // (with shadow), so every untouched header is unchanged.
    //  - elegant → white circle + shadow (current default).
    //  - modern  → light-grey circle + border, no shadow; chevron back + circular-cross
    //              close. Same look as the simple variant's `outlineButtons`, which is
    //              why the classes below match it exactly.
    //  - plain   → bare icons, no circle, no shadow.
    const isModern = buttonStyle === 'modern'
    const isPlain = buttonStyle === 'plain'

    const goBackBase = isModern
        ? 'cursor-pointer flex items-center justify-center w-9 h-9 rounded-full bg-[#F3F4F6] border border-[#CFCDCD] p-0'
        : isPlain || isLessFont
        ? 'border-0 bg-transparent p-0 cursor-pointer flex items-center justify-center'
        : 'w-12 h-12 border-0 outline-0 rounded-full flex items-center justify-center cursor-pointer p-0 ' +
          (isDark ? 'bg-transparent' : 'bg-white shadow-[0_0_30px_10px_#efefef]')
    // modern/plain carry their own icon colour; the dark-header invert is elegant-only.
    const goBackImg = isDark && !isModern && !isPlain ? '[filter:brightness(0)_invert(1)]' : ''
    const iconSizeCls = isModern ? 'w-4 h-4 max-md:w-3.5 max-md:h-3.5' : ''
    // Same size on a phone as on desktop, like the plugin's `button.go_back`.
    const backMobileCls = ''
    const closeMobileCls = ''
    const backSrc = isModern ? chevronLeft : longArrowLeft
    const closeSrcBooking = isModern ? boldCircleCross : bigClose

    const showLeftBack = overlayMode ? false : ((!isEditMode || !enableEditModeOnBackButton) && showBackButton)
    // Edit mode hides the ✕ so the passenger can't abandon the booking modal half-edited
    // — they leave through orderDetails. That rule is about the SHELL header only. An
    // `inline` header belongs to a nested .upper-modal popup, where ✕ closes just that
    // popup; suppressing it there left popups (the arrivals guide, Track Driver, the
    // perks views) with an empty header and NO way out at all.
    const showCancel = overlayMode ? true : (cancelButton && (inline || !isEditMode || ignoreEditMode))
    // buttonStyle implies the close type — modern→icon, plain→text — so callers using
    // buttonStyle don't need cancelButtonType. Existing elegant headers still pass it.
    const effCancelType = overlayMode
        ? 'icon'
        : (cancelButtonType || (isModern ? 'icon' : isPlain ? 'text' : undefined))

    // Empty left slot (no back button, no helper icon) but a title: a centred title then
    // reads as off-centre against the lone ✕ and wraps early, so drop the empty slot and
    // left-align the title into the freed space. Pass centerTitle to keep it centred.
    const titleLeft = !!title && !leftIcon && !showLeftBack && !props.centerTitle

    // The title is the middle item of a `justify-between` row, so it only reads as centred
    // while the slots either side reserve the SAME width — the plugin's `.inner`
    // min-width: 120px on both. Two things broke that here: the reservation was 50px, and
    // the icon-close branch dropped it altogether, so a step with `cancelButtonType="icon"`
    // and no ✕ (passengerQuantity, travelingDateTime…) collapsed its right slot to zero and
    // pushed the title ~25px right. `.icon` on the left is the plugin's own exception: a
    // helper icon sizes to itself (min-width: auto).
    const leftSlotCls = leftIcon
        ? ''
        : isSpacialRequest || titleLeft
        ? 'hidden'
        : 'min-w-[120px]'
    const rightSlotCls = effCancelType === 'icon'
        ? 'min-w-[120px] flex justify-end'
        : 'min-w-[120px]'

    const titleAlignCls = titleLeft ? 'items-start text-left' : 'items-center text-center'
    const titleCls = isLessFont
        ? `flex flex-col-reverse ${titleAlignCls} m-0 text-[22px] font-semibold justify-center max-md:hidden`
        : `flex flex-col-reverse ${titleAlignCls} m-0 text-[26px] font-semibold min-h-12 justify-center max-md:hidden`

    const headerNode = (
        <div className={wrapperCls}>
            <div className="flex items-center justify-between text-sm">
                <div className={leftSlotCls}>
                    {leftIcon ? (
                        // A helper icon can replace the back button on the left (e.g. the
                        // policy/gear icon). It renders PLAIN — no circle/border/bg; only the
                        // real back/close buttons get the buttonStyle circle treatment.
                        <img alt={leftIconAlt || ''} src={leftIcon}/>
                    ) : showLeftBack ? (
                        <button onClick={handleBack} className={goBackBase}>
                            <img alt="Go back" src={backSrc} className={`${goBackImg} ${iconSizeCls} ${backMobileCls}`}/>
                        </button>
                    ) : null}
                </div>

                {/* `header-title` carries no styles of its own here — it's the hook the
                    ported SCSS uses to size a single step's title (e.g.
                    `.step-travelingDateTime .header-title h2`), the same class name the
                    plugin's header renders. */}
                <div className={`header-title ${titleLeft ? 'flex-1 min-w-0' : ''}`}>
                    {title && <h2 className={titleCls}>{title}</h2>}
                </div>

                <div className={rightSlotCls}>
                    {showCancel && effCancelType === 'text' && (
                        <button
                            onClick={closeModal}
                            className="text-[#8d8e8f] border-0 bg-transparent p-0 flex items-center text-base max-md:text-sm cursor-pointer ml-auto"
                        >
                            Close
                            <img src={bigCloseLight} alt="Close Popup" className="ml-2.5"/>
                        </button>
                    )}
                    {showCancel && effCancelType === 'icon' && (
                        <button onClick={closeModal} className={`${goBackBase}${cancelDesktopOnly ? ' max-md:hidden' : ''}`}>
                            <img src={closeSrcBooking} alt="Close Popup" className={`${goBackImg} ${iconSizeCls} ${closeMobileCls}`}/>
                        </button>
                    )}
                </div>
            </div>

            {/* Booking-progress bar — the plugin header's `.hopn-modal-header-bottom >
                .total-steps-ui`. Rebuilt in Tailwind rather than emitting those class
                names: the ported SCSS nests them under `.hopn-modal-header`, which this
                header doesn't render, and adding that class would drag in a whole second
                set of paddings/backgrounds on top of the Tailwind ones. Geometry matches
                the original — 195px track, 6px tall, #B1FBE3 segments fixed at 20% each
                on a #CFCDCD rail.
                Hidden in edit mode for the same reason the back arrow is: the passenger
                jumps to one section from orderDetails and comes straight back, so a
                "step 2 of 5" bar measures a journey they aren't on. Hidden under md to
                match the plugin's 767 rule. */}
            {totalSteps > 0 && !isEditMode && (
                <div className="text-center max-md:hidden">
                    <div className="relative flex gap-2.5 max-w-[195px] h-1.5 mx-auto rounded-full bg-[#CFCDCD] overflow-hidden">
                        {Array.from({length: totalSteps}).map((_, i) => (
                            <span key={i} className="w-1/5 bg-[#B1FBE3] rounded-[50px]"/>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )

    if (insideShell) return createPortal(headerNode, bookingShell.headerEl)
    return headerNode
}

export default ModalHeader
